import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormIcon, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { type LoginFormSchema, loginFormSchema } from "@/features/dashboard/types/user";
import { useAuth } from "@/hooks/use-auth.tsx";
import { useValidationError } from "@/hooks/use-validation-errors";
import { sleep } from "@/utils/sleep.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconLock, IconUser } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useForm } from "react-hook-form";

export const Route = createFileRoute("/forgot-password")({
	component: LoginPage,
});

function LoginPage() {
	const devMode = import.meta.env.DEV;
	const { login } = useAuth();
	const navigate = useNavigate({ from: "/login" });
	const [errors, setErrors, clearError, getFirstError] = useValidationError();

	const handleSubmit = async (data: LoginFormSchema) => {
		clearError();

		try {
			await login(data);

			await sleep(1000);

			await navigate({
				to: "/",
			});
		} catch (e) {
			form.setValue("password", "");

			if (e instanceof AxiosError) {
				setErrors(e.response?.data.errors);
			}
		}
	};

	const form = useForm({
		resolver: zodResolver(loginFormSchema),
		defaultValues: {
			username: devMode ? "superuser" : "",
			password: devMode ? "password" : "",
			remember: true,
		},
		mode: "onChange",
	});

	return (
		<div className="flex flex-row w-full h-[100svh] p-4 bg-background">
			<div className="relative flex flex-col w-1/2 h-full rounded-lg bg-slate-100 overflow-hidden">
				<div className="absolute inset-0 flex flex-col justify-center items-center w-full h-full p-8">
					<div className="relative flex flex-col justify-center items-center w-full max-w-[420px] gap-6">
						<div className="z-[2] flex flex-col justify-center items-center w-full gap-2">
							<h1 className="text-3xl font-bold text-center text-gray-800">Case Management System</h1>
							<p className="text-lg text-foreground text-center text-gray-700">
								Secure access to the Case Management System. Please log in to ensure data integrity and confidentiality.
							</p>
						</div>
						<div className="z-[2] inline-flex py-2 px-3 rounded-md bg-primary-600">
							<span className=" font-semibold text-sm text-white">Version 0.0.1</span>
						</div>
						<div className="z-[1] absolute inset-0 left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-primary-100 blur-2xl opacity-50" />
					</div>
				</div>
			</div>
			<div className="flex flex-col justify-center items-center w-1/2 p-6">
				<div className="flex w-full max-w-[420px]">
					<Form {...form}>
						<form onSubmit={form.handleSubmit(handleSubmit)}>
							<Alert type="warning" allowClose={true} title="You are in development mode!" icon={<IconAlertCircle size={20} stroke={2} />}>
								If you are seeing this message, you are in development mode. Do not store any sensitive information.
							</Alert>
							<div className="grid grid-cols-12 w-full gap-3">
								<div className="col-span-12 flex flex-col w-full">
									<FormField
										control={form.control}
										name="username"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Username or Email</FormLabel>
												</div>
												<FormGroup iconPosition="left">
													<FormIcon>
														<IconUser size={18} stroke={1.5} />
													</FormIcon>
													<Input autoComplete="off" placeholder="Username or Email" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-12 flex flex-col w-full">
									<FormField
										control={form.control}
										name="password"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Password</FormLabel>
												</div>
												<FormGroup iconPosition="left">
													<Input type="password" placeholder="Password" {...field} />
													<FormIcon>
														<IconLock size={18} stroke={1.5} />
													</FormIcon>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-12 flex flex-col w-full mt-1 gap-2">
									<Button type="submit" className="w-full">
										Sign-in
									</Button>
									<Link to="/forgot-password">
										<Button className="w-full" variant="ghost">
											Forgot Password
										</Button>
									</Link>
								</div>
							</div>
							<div className="flex flex-row items-center justify-center w-full my-6">
								<hr className="w-full h-px border-t border-border" />
								<div className="flex-shrink-0 whitespace-nowrap text-sm uppercase text-muted-foreground text-center px-3">or Sign-in with</div>
								<hr className="w-full h-px border-t border-border" />
							</div>
							<div className="grid grid-cols-12 w-full gap-2">
								<div className="col-span-12 flex w-full">
									<Button className="w-full" variant="ghost">
										Sign-in with Passkey
									</Button>
								</div>
							</div>
						</form>
					</Form>
				</div>
			</div>
		</div>
	);
}
